import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ChevronRight, ExternalLink } from "lucide-react";
import { Layout } from "@/components/Layout";
import { DisplayAd } from "@/components/DisplayAd";

export type Crumb = { label: string; to?: string };

export function HubShell({
  crumbs,
  step,
  totalSteps = 3,
  eyebrow,
  title,
  intro,
  children,
  back,
  next,
}: {
  crumbs: Crumb[];
  step: number;
  totalSteps?: number;
  eyebrow: string;
  title: string;
  intro: string;
  children: React.ReactNode;
  back?: { to: string; label: string };
  next?: { to: string; label: string };
}) {
  return (
    <Layout>
      <article className="mx-auto max-w-4xl px-4 py-8 md:py-12">
        <nav aria-label="Breadcrumb" className="mb-6 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
          {crumbs.map((c, i) => (
            <span key={c.label} className="inline-flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              {c.to ? (
                <Link to={c.to} className="hover:text-primary">{c.label}</Link>
              ) : (
                <span aria-current="page" className="text-foreground">{c.label}</span>
              )}
            </span>
          ))}
        </nav>

        <div className="mb-3 flex items-center gap-2">
          <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary">{eyebrow}</span>
          <span className="text-xs text-muted-foreground">Step {step} of {totalSteps}</span>
        </div>
        <div className="mb-6 flex gap-1.5" aria-hidden>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i < step ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>

        <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">{title}</h1>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">{intro}</p>

        <DisplayAd placement="mid-billboard" className="my-8" />

        <div className="space-y-10">{children}</div>

        <DisplayAd placement="inline-2" className="my-10" />

        <div className="mt-10 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
          {back ? (
            <Link to={back.to} className="inline-flex items-center justify-center gap-2 rounded-xl border border-border px-5 py-3 text-sm font-semibold hover:bg-muted">
              <ArrowLeft className="h-4 w-4" /> {back.label}
            </Link>
          ) : <span />}
          {next && (
            <Link to={next.to} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              {next.label} <ArrowRight className="h-4 w-4" />
            </Link>
          )}
        </div>

        <p className="mt-10 rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground leading-relaxed">
          <b>Independent guide.</b> DrHint is not affiliated with any company listed here and earns nothing from sign-ups.
          Pay rates, regions and hiring status change often — always confirm on the official website before applying.
          Real companies never ask you to pay a fee to get hired or to unlock tasks.
        </p>
      </article>
    </Layout>
  );
}

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-4 text-2xl font-bold">{title}</h2>
      <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export function InfoGrid({ items }: { items: { title: string; body: string }[] }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {items.map((i) => (
        <div key={i.title} className="rounded-2xl border border-border bg-card p-5">
          <h3 className="font-semibold text-foreground">{i.title}</h3>
          <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{i.body}</p>
        </div>
      ))}
    </div>
  );
}

export type PortalRow = {
  name: string;
  url: string;
  type: string;
  pay: string;
  regions: string;
  badge: string;
};

export function PortalTable({ rows }: { rows: PortalRow[] }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-border">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-card text-left">
          <tr>
            <th className="px-4 py-3 font-semibold">Company</th>
            <th className="px-4 py-3 font-semibold">Work type</th>
            <th className="px-4 py-3 font-semibold">Reported pay</th>
            <th className="px-4 py-3 font-semibold">Regions</th>
            <th className="px-4 py-3 font-semibold">Apply</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name} className="border-t border-border align-top">
              <td className="px-4 py-3">
                <div className="font-semibold text-foreground">{r.name}</div>
                <span className="mt-1 inline-block rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">{r.badge}</span>
              </td>
              <td className="px-4 py-3 text-muted-foreground">{r.type}</td>
              <td className="px-4 py-3 text-muted-foreground">{r.pay}</td>
              <td className="px-4 py-3 text-muted-foreground">{r.regions}</td>
              <td className="px-4 py-3">
                <a href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
                  Official site <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FaqAccordion({ faqs }: { faqs: { q: string; a: string }[] }) {
  return (
    <div className="space-y-3">
      {faqs.map((f) => (
        <details key={f.q} className="group rounded-xl border border-border bg-card p-4">
          <summary className="cursor-pointer list-none font-semibold text-foreground flex justify-between gap-3">
            {f.q}
            <ChevronRight className="h-4 w-4 shrink-0 transition-transform group-open:rotate-90" />
          </summary>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

export function hubHead(path: string, title: string, description: string) {
  const url = `https://drhint.com${path}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}
